/** 媒体收集：把 data URL / Blob 转成 SHA-256 去重的原始字节与 `asset:` 引用。 */
import { bytesToBase64, base64ToBytes, sha256Hex } from './codec';
import type { BackupMediaEntry } from './types';

export interface MediaFile {
  bytes: Uint8Array;
  mime: string;
  ext: string;
}

export interface ParsedDataUrl {
  mime: string;
  bytes: Uint8Array;
}

export function mimeToExtension(mime: string): string {
  const normalized = mime.split(';')[0]?.trim().toLowerCase() ?? '';
  const known: Record<string, string> = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'image/svg+xml': 'svg',
    'image/avif': 'avif',
    'image/bmp': 'bmp',
    'audio/mpeg': 'mp3',
    'audio/mp3': 'mp3',
    'audio/wav': 'wav',
    'audio/x-wav': 'wav',
    'audio/ogg': 'ogg',
    'audio/mp4': 'm4a',
    'audio/x-m4a': 'm4a',
    'audio/webm': 'webm',
    'video/mp4': 'mp4',
    'application/pdf': 'pdf',
    'application/json': 'json',
    'text/plain': 'txt',
  };
  return known[normalized] ?? 'bin';
}

export function parseDataUrl(dataUrl: string): ParsedDataUrl | null {
  const match = /^data:([^,]*?),(.*)$/s.exec(dataUrl);
  if (match === null) return null;
  const meta = match[1] ?? '';
  const body = match[2] ?? '';
  const mime = meta.split(';')[0]?.trim() ?? 'application/octet-stream';
  const isBase64 = /(?:^|;)base64(?:;|$)/i.test(meta);
  if (isBase64) {
    const bytes = base64ToBytes(body);
    return bytes === null ? null : { mime, bytes };
  }
  try {
    return { mime, bytes: new TextEncoder().encode(decodeURIComponent(body)) };
  } catch {
    return { mime, bytes: new TextEncoder().encode(body) };
  }
}

export function bytesToDataUrl(bytes: Uint8Array, mime: string): string {
  return `data:${mime};base64,${bytesToBase64(bytes)}`;
}

/** 单次备份内的媒体收集器：同 SHA-256 只保留一份原始字节。 */
export class MediaCollector {
  private readonly media = new Map<string, MediaFile>();
  readonly warnings: string[] = [];

  async addDataUrl(
    dataUrl: string,
    fallbackMime: string,
    label: string,
  ): Promise<string | null> {
    const parsed = parseDataUrl(dataUrl);
    if (parsed === null) {
      this.warnings.push(`${label} 不是可解析的 data URL，已原样保留`);
      return null;
    }
    return this.addBytes(parsed.bytes, parsed.mime || fallbackMime, label);
  }

  async addBlob(blob: Blob, label: string): Promise<string | null> {
    try {
      const buffer =
        typeof blob.arrayBuffer === 'function'
          ? await blob.arrayBuffer()
          : await new Promise<ArrayBuffer>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result: unknown = reader.result;
                if (
                  result !== null &&
                  typeof result === 'object' &&
                  'byteLength' in result
                ) {
                  resolve(result as ArrayBuffer);
                } else {
                  reject(new Error('无法读取 Blob'));
                }
              };
              reader.onerror = () => reject(reader.error ?? new Error('无法读取 Blob'));
              reader.readAsArrayBuffer(blob);
            });
      return this.addBytes(new Uint8Array(buffer), blob.type || 'application/octet-stream', label);
    } catch {
      this.warnings.push(`${label} 读取失败，已跳过`);
      return null;
    }
  }

  private async addBytes(
    bytes: Uint8Array,
    mime: string,
    label: string,
  ): Promise<string | null> {
    if (bytes.length === 0) {
      this.warnings.push(`${label} 内容为空，已跳过`);
      return null;
    }
    const sha256 = await sha256Hex(bytes);
    if (!this.media.has(sha256)) {
      this.media.set(sha256, { bytes, mime, ext: mimeToExtension(mime) });
    }
    return `asset:${sha256}`;
  }

  entries(): BackupMediaEntry[] {
    return [...this.media.entries()]
      .map(([sha256, file]) => ({
        sha256,
        mime: file.mime,
        bytes: file.bytes.length,
        ext: file.ext,
      }))
      .sort((a, b) => (a.sha256 < b.sha256 ? -1 : a.sha256 > b.sha256 ? 1 : 0));
  }

  bytes(): Map<string, Uint8Array> {
    return new Map([...this.media.entries()].map(([sha256, file]) => [sha256, file.bytes]));
  }

  get size(): number {
    return this.media.size;
  }

  get byteLength(): number {
    return [...this.media.values()].reduce((sum, file) => sum + file.bytes.length, 0);
  }
}
